import axios from 'axios'
import Footer from 'components/layout/Footer'
import PreviewNotice from 'components/layout/PreviewNotice'
import BodyRenderer from 'components/renderers/BodyRenderer'
import HeaderContainer from 'components/renderers/HeaderContainer'
import HeroRenderer from 'components/renderers/HeroRenderer'
import PageSeo from 'components/seo/PageSeo'
import Head from 'next/head'
import Env from 'Env'
import HTTPMethod from 'http-method-enum'
import { TopPosterModel } from 'models/body/TopPosterModel'
import { AppInfoModel } from 'models/config/AppInfoModel'
import { MenuModel } from 'models/menu/MenuModel'
import { PageModel } from 'models/page/PageModel'
import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import React, { Fragment, FunctionComponent, ReactElement } from 'react'
import { ApiPromise } from 'services/ApiPromise'
import { Converters } from 'services/Converters'
import { useRouter } from 'next/router'
import ErrorPage from 'next/error'
import TextBlocksWithFeatureImage from 'components/body/TextBlocksWithFeatureImage'
import loadash from 'lodash'
import Markdown from 'markdown-to-jsx'
import { GridSectionData } from 'types/generatedTypes'
import { ThemeType } from 'models/enums/ThemeType'
import ResponsiveImage from 'components/core/ResponsiveImage'
import { GetThemeClasses } from 'utils/GetThemeClasses'
import GridSection from 'components/body/GridSection'

type PathsParamsType = {
  params: { slug: string }
}

type Props = {
  page: PageModel
  menu: MenuModel
  footer: MenuModel
  isPreview: boolean
  posters: {
    data: TopPosterModel[]
  }
  appConfig: AppInfoModel
}

const DynamicPage: NextPage<Props> = (props) => {
  const router = useRouter()
  if (!router.isFallback && !props.page) {
    return <ErrorPage statusCode={404} />
  }

  const pageAttributes = props.page.data.attributes
  const posters = props.posters.data
  const bodyData = loadash.get(pageAttributes, 'body')
  const gridSection = loadash.find(bodyData, {
    elementSlug: 'grid-section-with-title',
  })

  const nonGridSection = loadash.reject(bodyData, {
    elementSlug: 'grid-section-with-title',
  })
  return (
    <Fragment>
      <Head>
        <link
          rel="canonical"
          href={`${Env.SiteUrl}/${props.page.data.attributes.slug}/`}
        />
      </Head>
      <PageSeo title={pageAttributes.title} seo={pageAttributes.seo} />
      {props.isPreview ? <PreviewNotice /> : null}

      <HeaderContainer posters={props.posters} menu={props.menu} />

      {pageAttributes.hero.map((hero, index) => {
        return <HeroRenderer key={index} data={hero} />
      })}
      {gridSection ? <GridSection data={gridSection} /> : null}
      {nonGridSection.map((section, index) => {
        return <BodyRenderer key={index} data={section} />
      })}
      <Footer data={props.footer} />
    </Fragment>
  )
}

export const getStaticProps: GetStaticProps = async (context) => {
  const isPreview = context.preview
  const slug = context.params?.slug as string
  const allPromises = ApiPromise.Page.GetPageWithHeaderFooter(slug, isPreview)
  const [menuPromise, pagePromise, footerPromise, postersPromise] =
    await Promise.all(allPromises)
  const [menu, page, footer, posters] =
    Converters.KeysConvert.ConvertAllToLower([
      menuPromise.data,
      pagePromise.data,
      footerPromise.data,
      postersPromise.data,
    ])
  const appConfig = await ApiPromise.Layout.AppConfig()
  return {
    props: {
      page,
      menu,
      footer,
      posters,
      appConfig,
      isPreview: isPreview ? true : false,
    },
    revalidate: Env.PropsRevalidateTime,
  }
}

export const getStaticPaths: GetStaticPaths = async (context) => {
  const allPagesRoute = '/pages?pagination[page]=1&pagination[pageSize]=100'
  const resp = await axios({
    method: HTTPMethod.GET,
    baseURL: Env.ApiUrl,
    url: allPagesRoute,
  })
  const pageRecordsData: any[] = resp.data['data']
  const pageSlugRecords: PathsParamsType[] = []
  pageRecordsData.map((page) => {
    const isThePageHome = page.attributes.Slug === 'home'
    if (!isThePageHome) {
      pageSlugRecords.push({ params: { slug: page.attributes.Slug } })
    }
  })

  return {
    paths: pageSlugRecords,
    fallback: false,
  }
}

export default DynamicPage
